import auremLogo from "@aurem/components/src/assets/images/aurem.svg";
import { Text } from "@aurem/ui";
import { useUserData, useUserPermissions } from "graphql/user";
import { useEnvironmentConfig } from "pages/Controls/hooks/environment.hooks";
import { useI18n } from "providers/I18nProvider";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PermissionEnum } from "types";
import { useBasePath } from "../../../hooks/useBasePath";
import { ControlsNavList } from "./ControlsNavList";
import { NavLink } from "./types";

const LayoutOnboardingSidebarView = () => {
  const { t } = useI18n();
  const { config } = useEnvironmentConfig();
  const { path } = useBasePath();
  const { userPermissions, permissionsContain } = useUserPermissions();

  const navLinks: NavLink[] = [];

  if (permissionsContain(userPermissions, [PermissionEnum.ManageBranding])) {
    navLinks.push({
      label: t("apps.admin.navBar.configTools.sidebar.configureBrand"),
      to: path("/controls/branding"),
      disabled: false,
    });
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ManageDocument])) {
    navLinks.push({
      label: t("apps.admin.navBar.configTools.sidebar.uploadDocuments"),
      to: path("/controls/documents"),
      disabled: !config?.id,
    });
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ManageProduct])) {
    navLinks.push({
      label: t("apps.admin.navBar.configTools.sidebar.chooseProducts"),
      to: path("/controls/products"),
      disabled: !config?.id,
    });
    navLinks.push({
      label: t("apps.admin.navBar.configTools.sidebar.automations"),
      to: path("/controls/automations"),
      disabled: !config?.id,
    });
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ManageForms])) {
    navLinks.push({
      label: t("apps.admin.navBar.configTools.sidebar.customizeFormFields"),
      to: path("/controls/custom-attributes"),
      disabled: !config?.id,
    });
  }

  if (
    permissionsContain(userPermissions, [PermissionEnum.ManagePasswordPolicy])
  ) {
    navLinks.push({
      label: t("apps.admin.navBar.subNavBar.accessAndRestrictions"),
      to: path("/controls/access-and-restrictions"),
      disabled: !config?.id,
    });
  }

  return (
    <aside className="w-full h-full">
      <div className="flex items-center px-6 h-16">
        <img src={auremLogo} alt="logo" className="h-5 object-fill" />
      </div>

      <div className="h-[3.3125rem] px-6 flex items-center justify-between border-y border-neutral-500/10">
        <Text
          size="xs"
          weight="medium"
          className="text-neutral-500/50 uppercase"
        >
          {t("apps.admin.navBar.configTools.sidebar.digitalExperience")}
        </Text>
        <span className="rounded-full bg-neutral-500/5 text-xxs font-medium py-1 px-2 text-neutral-500/80">
          {navLinks.length}
        </span>
      </div>

      <nav aria-label="Controls navigation" className="px-6 py-4">
        <ControlsNavList navLinks={navLinks} />
      </nav>
    </aside>
  );
};

export const LayoutOnboardingSidebar = () => {
  const { pathname } = useLocation();
  const { config, loading } = useEnvironmentConfig();
  const { user } = useUserData();

  const isPlatformAdmin = useMemo(() => {
    if (!user?.userRoles) return false;
    return user.userRoles.some((userRole) => userRole.role.isPlatform);
  }, [user]);

  const isOnControlsRoute = pathname.includes("/controls");

  const shouldRender =
    config?.firstLogin && isOnControlsRoute && isPlatformAdmin;

  if (loading || !shouldRender) return null;

  return (
    <div className="w-[300px] border-r border-neutral-500/10">
      <LayoutOnboardingSidebarView />
    </div>
  );
};
